import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';

import { useGetLeaderboardQuery, useGetMeQuery } from '@/services/qgammonApi';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { useQgammonRitmo, useQgammonVerdetto } from '@/hooks/useQgammonLista';
import { cn } from '@/lib/cn';

/**
 * Hub "Classifica" section for Backgammon: ELO leaderboard, own row highlighted, and the caller's
 * own row PINNED under the list — «dove sono io» senza scorrere fino a trovarsi.
 *
 * ⚠️ Il server manda i primi N (default 50, `QGammon matches/routes.ts`) e NON la posizione di chi
 * sta fuori: in quel caso la riga fissata dice «oltre l'N°» con N = righe ricevute, e non inventa un
 * numero. La posizione esatta arrivera' con `GET /me/stats` (KB, proposta Hub backgammon §4.2).
 *
 * I cinque casi e il polling: `useQgammonLista`. La frase d'errore resta quella di prima — la
 * scadenza dei 45 s cambia QUANDO compare, mai QUALE frase e'.
 */
export function QgammonLeaderboardPanel() {
  const { t } = useTranslation('backgammon');
  const ritmo = useQgammonRitmo();
  const q = useGetLeaderboardQuery(undefined, ritmo.opts);
  const { data: me } = useGetMeQuery();
  const myId = me?.user.id ?? null;
  const players = q.data?.players ?? [];
  const verdict = useQgammonVerdetto(ritmo, q, players.length);

  if (verdict === 'loading' || verdict === 'slow') {
    return <PanelWaiting slow={verdict === 'slow'} />;
  }
  if (verdict === 'error' || verdict === 'unavailable') {
    return (
      <p className="mt-2 rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger">
        {t('home.loadError')}
      </p>
    );
  }
  if (verdict === 'empty') {
    return (
      <p className="mt-2 rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
        {t('home.empty')}
      </p>
    );
  }

  const myIndex = myId === null ? -1 : players.findIndex((p) => p.id === myId);
  const mineInList = myIndex >= 0 ? players[myIndex] : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 py-2">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-bg-secondary">
          {players.map((p, i) => {
            const mine = myId !== null && p.id === myId;
            return (
              <li
                key={p.id}
                className={cn('flex items-center gap-3 px-4 py-2.5', mine && 'bg-brand/15')}
              >
                <span className="w-7 text-sm tabular-nums text-fg-muted">{i + 1}</span>
                <span className={cn('flex-1 truncate text-sm', mine ? 'font-semibold text-brand' : 'text-fg')}>
                  {p.displayName || p.username}
                </span>
                {mine && <Trophy aria-hidden className="h-3.5 w-3.5 text-brand" />}
                <span className="text-sm font-semibold tabular-nums text-brand">{p.elo}</span>
              </li>
            );
          })}
        </ul>
      </div>
      {/* La riga fissata esiste solo se il server ha detto chi sono: senza `me` non si afferma
          niente su di me. */}
      {me && (
        <div
          data-testid="bg-classifica-tu"
          className="flex shrink-0 items-center gap-3 rounded-xl border border-brand/40 bg-brand/15 px-4 py-2.5"
        >
          <span className="shrink-0 text-sm font-semibold tabular-nums text-fg-muted">
            {mineInList
              ? myIndex + 1
              : t('hubPanels.beyondTop', { n: players.length, defaultValue: 'Below the top {{n}}' })}
          </span>
          <span className="flex-1 truncate text-sm font-semibold text-brand">
            {t('hubPanels.you', { defaultValue: 'You' })}
          </span>
          <span className="text-sm font-semibold tabular-nums text-brand">
            {mineInList ? mineInList.elo : me.user.elo}
          </span>
        </div>
      )}
    </div>
  );
}
